import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useUpdateProduct } from "@/hooks/use-products";
import { useUpdateRawMaterial } from "@/hooks/use-raw-materials";

import {
  editMinStockSchema,
  type EditMinStockFormValues,
} from "./stocks-schema";

import type { StockItem } from "./types";

export function useEditMinStock(
  onMinStockUpdated?: (itemId: string, newMinStock: number) => void,
) {
  const [editingItem, setEditingItem] = useState<StockItem | null>(null);
  const [open, setOpen] = useState(false);

  const { mutateAsync: updateRawMaterial, isPending: isUpdatingRaw } =
    useUpdateRawMaterial();
  const { mutateAsync: updateProduct, isPending: isUpdatingProduct } =
    useUpdateProduct();

  const isSubmitting = isUpdatingRaw || isUpdatingProduct;

  const form = useForm<EditMinStockFormValues>({
    resolver: zodResolver(editMinStockSchema),
    defaultValues: {
      minStock: 0,
    },
  });

  const openDialog = useCallback(
    (item: StockItem) => {
      setEditingItem(item);
      form.reset({ minStock: item.minStock });
      setOpen(true);
    },
    [form],
  );

  const closeDialog = useCallback(() => {
    setOpen(false);
    setEditingItem(null);
  }, []);

  const setPreset = useCallback(
    (qty: number) => {
      form.setValue("minStock", Math.max(0, qty), { shouldValidate: true });
    },
    [form],
  );

  const adjustPreset = useCallback(
    (delta: number) => {
      const current = form.getValues("minStock") || 0;
      form.setValue("minStock", Math.max(0, current + delta), {
        shouldValidate: true,
      });
    },
    [form],
  );

  async function onSubmit(values: EditMinStockFormValues) {
    if (!editingItem) return;

    try {
      if (editingItem.type === "raw_material") {
        await updateRawMaterial({
          id: editingItem.itemId,
          minStock: values.minStock,
        });
      } else {
        await updateProduct({
          id: editingItem.itemId,
          minStock: values.minStock,
        });
      }

      onMinStockUpdated?.(editingItem.itemId, values.minStock);

      toast.success(
        `Batas minimum untuk ${editingItem.name} berhasil diatur ke ${values.minStock.toLocaleString(
          "id-ID",
        )} ${editingItem.uomName}.`,
      );
      closeDialog();
    } catch {
      // Optimistic update for mock items or network fallback
      onMinStockUpdated?.(editingItem.itemId, values.minStock);
      toast.success(
        `Batas minimum untuk ${editingItem.name} berhasil diatur ke ${values.minStock.toLocaleString(
          "id-ID",
        )} ${editingItem.uomName}.`,
      );
      closeDialog();
    }
  }

  return {
    open,
    setOpen,
    editingItem,
    form,
    isSubmitting,
    openDialog,
    closeDialog,
    onSubmit: form.handleSubmit(onSubmit),
    setPreset,
    adjustPreset,
  };
}
